"use client";

import { useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

interface PricingTier {
  id: string;
  name: string;
  description: string;
  features: string[];
  cta: string;
  popular?: boolean;
}

export default function PricingTiers() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const tiers: PricingTier[] = [
    {
      id: "tier_basic",
      name: "Basic",
      description: "Perfect for small projects and independent creators",
      features: [
        "Single camera setup",
        "Basic lighting package",
        "Standard editing",
        "Up to 3 revision rounds",
        "2-week turnaround",
        "HD delivery",
      ],
      cta: "Starting from $5,000",
    },
    {
      id: "tier_professional",
      name: "Professional",
      description: "Ideal for corporate events and commercial productions",
      features: [
        "Multi-camera setup",
        "Professional lighting & audio",
        "Advanced editing & color grading",
        "Unlimited revisions",
        "1-week turnaround",
        "4K delivery",
        "Motion graphics included",
        "Dedicated project manager",
      ],
      cta: "Starting from $15,000",
      popular: true,
    },
    {
      id: "tier_premium",
      name: "Premium",
      description: "Full-scale productions with complete creative control",
      features: [
        "Cinema-grade equipment",
        "Full crew & technical team",
        "Advanced VFX & CGI",
        "Priority scheduling",
        "Expedited turnaround",
        "8K delivery available",
        "Marketing materials included",
        "24/7 support",
        "Archive & backup services",
      ],
      cta: "Custom Quote",
    },
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 px-6 md:px-12 bg-[#080808] border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-4xl font-display font-light text-white mb-4">
            Transparent Pricing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light">
            Choose the package that fits your project needs. All tiers include our commitment to excellence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <div
              key={tier.id}
              className={`relative glass-card p-8 rounded-3xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl animate-on-scroll [animation:animationIn_0.8s_ease-out_${
                0.3 + index * 0.1
              }s_both] ${
                tier.popular ? "border-2 border-primary" : ""
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-primary to-secondary text-white text-xs font-medium uppercase tracking-wider">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-2xl font-display font-semibold text-white mb-2">
                  {tier.name}
                </h3>
                <p className="text-sm text-muted-foreground">{tier.description}</p>
              </div>

              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, idx) => (
                  <li
                    key={`${tier.id}_feature_${idx}`}
                    className="flex items-start gap-3 text-sm text-muted-foreground"
                  >
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-primary flex-shrink-0 mt-0.5"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-6 border-t border-white/10">
                <p className="text-xl font-display font-semibold text-white mb-4">
                  {tier.cta}
                </p>
                <Link
                  href="/contact"
                  className={`block w-full py-3 rounded-xl text-center font-medium transition-all ${
                    tier.popular
                      ? "bg-gradient-to-r from-primary to-red-800 text-white glow-primary hover:glow-primary-hover" :"border border-white/20 text-white hover:bg-white/10"
                  }`}
                >
                  Get Started
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center animate-on-scroll [animation:animationIn_0.8s_ease-out_0.6s_both]">
          <p className="text-muted-foreground text-sm mb-4">
            Need a custom package? We can tailor our services to your specific requirements.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
          >
            Contact Us for Custom Quote
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}